import api from './api';

export interface StudentSubscriptionCredit {
  subscriptionId: string;
  teacherAuthUserId: string;
  teacherDisplayName?: string;
  packageId?: string;
  packageName?: string;
  status: string;
  lessonCreditsBalance: number;
  quantityLessonsPerPeriod: number;
  currencyCode: string;
  createdAt: string;
  cancelAtPeriodEnd: boolean;
  canRequestRefund: boolean;
  refundIneligibleReason?: string;
}

export interface RefundRequestRow {
  id: string;
  subscriptionId?: string;
  productKind: string;
  status: string;
  reason?: string;
  createdAt: string;
}

export interface StudentLessonPackage {
  id: string;
  teacherAuthUserId: string;
  teacherDisplayName: string;
  name: string;
  description?: string;
  priceCents: number;
  currencyCode: string;
  lessonsPerPeriod: number;
  lessonDurationMinutes: number;
  billingType: string;
  canSubscribe: boolean;
  subscribeBlockedReason?: string;
}

export const studentBillingService = {
  async getLessonCredits(): Promise<StudentSubscriptionCredit[]> {
    const res = await api.get<StudentSubscriptionCredit[]>('/billing/student/lesson-credits');
    return res.data;
  },

  async getRefundRequests(): Promise<RefundRequestRow[]> {
    const res = await api.get<RefundRequestRow[]>('/billing/student/refund-requests');
    return res.data;
  },

  async requestSubscriptionRefund(subscriptionId: string, reason?: string): Promise<{ message: string }> {
    const res = await api.post<{ message: string }>(
      `/billing/student/lesson-subscriptions/${subscriptionId}/refund-request`,
      { reason }
    );
    return res.data;
  },

  async cancelSubscription(subscriptionId: string, reason?: string): Promise<void> {
    await api.post(`/billing/student/lesson-subscriptions/${subscriptionId}/cancel`, { reason });
  },

  async listLessonPackages(teacherAuthUserId?: string): Promise<StudentLessonPackage[]> {
    const res = await api.get<StudentLessonPackage[]>('/billing/student/lesson-packages', {
      params: teacherAuthUserId ? { teacherAuthUserId } : undefined,
    });
    return res.data;
  },

  async startPackageCheckout(
    packageId: string,
    redirectUrl?: string
  ): Promise<{ checkoutUrl: string }> {
    const res = await api.post<{ checkoutUrl?: string; url?: string }>('/billing/student/checkout', {
      packageId,
      redirectUrl,
    });
    const url = res.data.checkoutUrl ?? res.data.url;
    if (!url) throw new Error('No checkout URL returned');
    return { checkoutUrl: url };
  },

  async getPaymentStatus(packageId: string): Promise<{
    status: string;
    hasActiveSubscription: boolean;
    lessonCreditsBalance: number;
    message?: string;
  }> {
    const res = await api.get<{
      status: string;
      hasActiveSubscription: boolean;
      lessonCreditsBalance: number;
      message?: string;
    }>('/billing/student/payment-status', { params: { packageId } });
    return res.data;
  },
};
